import { useEffect, useRef, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { TenantProvider } from "@/context/tenant-context";
import { ScoutWidget } from "@/components/assistant/scout-widget";
import { PlatformTour } from "@/components/tour/platform-tour";
import { TenantSidebar } from "./tenant-sidebar";
import { CollapsibleSidebar } from "./collapsible-sidebar";
import { MobileShell } from "./mobile-shell";
import { TenantTopBar } from "./tenant-topbar";
import {
  TenantSettingsDialog,
  type TenantSettingsDialogState,
  type TenantSettingsSection,
} from "./tenant-settings-dialog";
import { AgentSettingsDrawer } from "./agent-settings-drawer";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useOpenAgentChat } from "@/hooks/use-open-agent-chat";

export function TenantShell() {
  const isMobile = useIsMobile();
  const [settingsDialog, setSettingsDialog] =
    useState<TenantSettingsDialogState>(null);
  // Agent settings open as a right-hand drawer, not the full settings dialog —
  // tweaking one agent shouldn't take over the whole screen with tenant nav.
  const [agentSettingsId, setAgentSettingsId] = useState<string | null>(null);
  const openAgentChat = useOpenAgentChat();

  // Opening another agent from inside the drawer (a sub-agent, the parent)
  // also selects it, exactly like clicking it in the sidebar's agent list.
  // The URL's agentId is what the sidebar highlights and the chat talks to;
  // before, only the drawer swapped, so the sidebar and chat stayed on the
  // first agent (2026-10-09).
  const openAgentInDrawer = (agentId: string) => {
    setAgentSettingsId(agentId);
    openAgentChat(agentId);
  };

  // And the other way round: when the URL's agent changes while the drawer is
  // open (Back or Forward is the only way, the drawer covers the sidebar),
  // the drawer follows it, so it never shows one agent over another's chat.
  const [searchParams] = useSearchParams();
  const urlAgentId = searchParams.get("agentId");
  const lastUrlAgentId = useRef(urlAgentId);
  useEffect(() => {
    if (lastUrlAgentId.current === urlAgentId) return;
    lastUrlAgentId.current = urlAgentId;
    setAgentSettingsId((open) => (open && open !== urlAgentId ? urlAgentId : open));
  }, [urlAgentId]);

  // Let guided walkthroughs (and Scout) open the settings dialog straight at a
  // section — the dialog has no route, so this event is the only entry point.
  useEffect(() => {
    const onOpenSettings = (e: Event) => {
      const detail =
        (e as CustomEvent<{
          kind?: "account" | "tenant";
          section?: TenantSettingsSection;
          /** Open that section's create form rather than its list. */
          create?: boolean;
        }>).detail ?? {};
      // Close the agent drawer first. The settings dialog covers the screen, so
      // a drawer left open behind it is still sitting there when the dialog is
      // dismissed — and the Add MCP button on an agent's Config tab opens this
      // dialog from inside that very drawer.
      setAgentSettingsId(null);
      setSettingsDialog(
        detail.kind === "account"
          ? { kind: "account" }
          : { kind: "tenant", section: detail.section, create: detail.create },
      );
    };
    window.addEventListener("proclaw:open-settings", onOpenSettings);
    return () => window.removeEventListener("proclaw:open-settings", onOpenSettings);
  }, []);

  return (
    <TenantProvider>
      <Toaster position="bottom-right" closeButton richColors />
      {isMobile ? (
        <MobileShell />
      ) : (
        <>
          <div className="flex h-screen w-screen overflow-hidden bg-background">
            <CollapsibleSidebar>
              <TenantSidebar
                onOpenAccountSettings={() => setSettingsDialog({ kind: "account" })}
                onOpenTenantSettings={() => setSettingsDialog({ kind: "tenant" })}
                onCreateAgent={() => {
                  // Same reason as above: don't leave the drawer behind the dialog.
                  setAgentSettingsId(null);
                  setSettingsDialog({ kind: "agent", create: true });
                }}
              />
            </CollapsibleSidebar>
            <main className="flex flex-1 flex-col overflow-hidden">
              <TenantTopBar
                onOpenAgentSettings={(agentId) => setAgentSettingsId(agentId ?? null)}
              />
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <Outlet />
              </div>
            </main>
          </div>
          <TenantSettingsDialog
            state={settingsDialog}
            onClose={() => setSettingsDialog(null)}
          />
          <AgentSettingsDrawer
            agentId={agentSettingsId}
            onClose={() => setAgentSettingsId(null)}
            onOpenAgent={openAgentInDrawer}
            onCreateSubagent={(parentAgentId) => {
              setAgentSettingsId(null);
              setSettingsDialog({ kind: "agent", subagentParentId: parentAgentId });
            }}
          />
          {/* Scout help assistant — fixed-position launcher, desktop only for now */}
          <ScoutWidget />
          {/* First-login walkthrough (welcome modal + spotlight steps) */}
          <PlatformTour />
        </>
      )}
    </TenantProvider>
  );
}
